import React, { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

interface KeyManagementContextType {
    courseKeys: Record<string, string>;
    getOrFetchCourseKey: (courseId: string) => Promise<string | null>;
    schoolKeys: Record<string, string>;
    getOrFetchSchoolKey: (boardId: string, schoolId: string) => Promise<string | null>;
}

const KeyManagementContext = createContext<KeyManagementContextType | undefined>(undefined);

export const KeyManagementProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Ephemeral in-memory RAM cache for Course DEKs
    const [courseKeys, setCourseKeys] = useState<Record<string, string>>({});

    const courseKeysRef = useRef(courseKeys);
    useEffect(() => {
        courseKeysRef.current = courseKeys;
    }, [courseKeys]);

    const activeRequestsRef = useRef<Record<string, Promise<string | null> | undefined>>({});
    const activeSchoolRequestsRef = useRef<Record<string, Promise<string | null> | undefined>>({});

    const getOrFetchCourseKey = useCallback(async (courseId: string): Promise<string | null> => {
        if (!courseId) return null;

        if (courseKeysRef.current[courseId]) {
            return courseKeysRef.current[courseId];
        }

        if (activeRequestsRef.current[courseId]) {
            return activeRequestsRef.current[courseId];
        }

        const fetchPromise = (async () => {
            try {// `[KMS] 🔐 Fetching DEK for course ${courseId}...`);
                const getCourseKeyFn = httpsCallable<{ courseId: string }, { dek: string }>(functions, 'getCourseKey');
                const result = await getCourseKeyFn({ courseId });

                const dek = result.data.dek;
                if (dek) {
                    setCourseKeys(prev => ({ ...prev, [courseId]: dek }));
                    return dek;
                }
            } catch (err) {
                console.error(`[KMS] Failed to fetch DEK for course ${courseId}:`, err);
            } finally {
                delete activeRequestsRef.current[courseId];
            }
            return null;
        })();

        activeRequestsRef.current[courseId] = fetchPromise;
        return fetchPromise;
    }, []);

    const [schoolKeys, setSchoolKeys] = useState<Record<string, string>>({});
    const schoolKeysRef = useRef(schoolKeys);
    useEffect(() => {
        schoolKeysRef.current = schoolKeys;
    }, [schoolKeys]);

    const getOrFetchSchoolKey = useCallback(async (boardId: string, schoolId: string): Promise<string | null> => {
        if (!boardId || !schoolId) return null;
        const cacheKey = `${boardId}_${schoolId}`;

        if (schoolKeysRef.current[cacheKey]) {
            return schoolKeysRef.current[cacheKey];
        }

        if (activeSchoolRequestsRef.current[cacheKey]) {
            return activeSchoolRequestsRef.current[cacheKey];
        }

        const fetchPromise = (async () => {
            try {// `[KMS] 🔐 Fetching DEK for school ${schoolId}...`);
                const getSchoolKeyFn = httpsCallable<{ boardId: string; schoolId: string }, { dek: string }>(functions, 'getSchoolKey');
                const result = await getSchoolKeyFn({ boardId, schoolId });

                const dek = result.data.dek;
                if (dek) {
                    setSchoolKeys(prev => ({ ...prev, [cacheKey]: dek }));
                    return dek;
                }
            } catch (err) {
                console.error(`[KMS] Failed to fetch DEK for school ${schoolId}:`, err);
            } finally {
                delete activeSchoolRequestsRef.current[cacheKey];
            }
            return null;
        })();

        activeSchoolRequestsRef.current[cacheKey] = fetchPromise;
        return fetchPromise;
    }, []);

    return (
        <KeyManagementContext.Provider value={{ courseKeys, getOrFetchCourseKey, schoolKeys, getOrFetchSchoolKey }}>
            {children}
        </KeyManagementContext.Provider>
    );
};

export const useKeyManagement = () => {
    const context = useContext(KeyManagementContext);
    if (!context) {
        throw new Error('useKeyManagement must be used within a KeyManagementProvider');
    }
    return context;
};
